import React from 'react';
import { Phone, Mail, Globe, MapPin, Linkedin, Twitter, Instagram, Facebook, Music2 } from 'lucide-react';
import UnifiedBrandIcon from './UnifiedBrandIcon';
import { ContactInfo, SocialLink } from '../types';

interface ContactSectionProps {
  contactInfo: ContactInfo;
  socialLinks: SocialLink[];
  user?: any;
  onShowEmail?: () => void;
  onAnalyticsUpdate?: (metric: string) => void;
}

const ContactSection: React.FC<ContactSectionProps> = ({ contactInfo, socialLinks, user, onShowEmail, onAnalyticsUpdate }) => {
  const contactItems = [
    {
      icon: Phone,
      value: contactInfo.phone,
      href: `tel:${contactInfo.phone}`,
  // Use dedicated callClicks for phone taps
  onClick: () => onAnalyticsUpdate?.('callClicks')
    },
    {
      icon: Mail,
      value: contactInfo.email,
      href: '#email',
      onClick: (e?: any) => {
        try { if (e?.preventDefault) e.preventDefault(); } catch {}
        if (onShowEmail) {
          onShowEmail();
        } else {
          // Fallback: show a toast instead of opening external client
          (window as any).toast?.('Email form unavailable');
        }
        onAnalyticsUpdate?.('messageClicks');
      }
    },
    {
      icon: Globe,
      // Guard against missing website
      value: (contactInfo.website || '').replace('https://', ''),
      href: contactInfo.website || '#',
      onClick: () => onAnalyticsUpdate?.('contactClicks')
    },
    {
      icon: MapPin,
      value: contactInfo.location || '',
      href: `https://maps.google.com/?q=${encodeURIComponent(contactInfo.location || '')}`,
      onClick: () => onAnalyticsUpdate?.('contactClicks')
    }
  ];

  const allowedSocials = new Set(['linkedin', 'twitter', 'instagram', 'facebook', 'tiktok', 'x']);
  const normalize = (s: string) => String(s || '').trim().toLowerCase();
  const alias = (p: string, url?: string) => {
    const k = normalize(p);
    const u = normalize(url || '');
    if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
    if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
    return k;
  };
  const getSocialFallback = (platform: string) => {
    const map = {
      linkedin: <Linkedin className="w-5 h-5" />,
      twitter: <Twitter className="w-5 h-5" />,
      instagram: <Instagram className="w-5 h-5" />,
      facebook: <Facebook className="w-5 h-5" />,
      tiktok: <Music2 className="w-5 h-5" />, // placeholder until custom icon provided
    } as const;
    return map[platform.toLowerCase() as keyof typeof map] ?? null;
  };

  return (
    <div className="px-6 mb-6">
      <div className="grid grid-cols-3 gap-4">
        {/* Contact Column */}
        <div className="col-span-2 bg-white p-4 rounded-lg border border-gray-200">
          <h3 
            className="text-sm font-semibold mb-3"
            style={{ color: user?.colors?.text || '#1f2937' }}
          >
            Contact
          </h3>
          <div className="space-y-3">
            {contactItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <a
                  key={index}
                  href={item.href}
                  onClick={item.onClick}
                  target={item.icon === Globe ? '_blank' : undefined}
                  rel={item.icon === Globe ? 'noopener noreferrer' : undefined}
                  className="flex items-center text-sm transition-colors"
                  style={{ color: user?.colors?.text || '#374151' }}
                >
                  <IconComponent 
                    className="w-4 h-4 mr-3 flex-shrink-0" 
                    style={{ color: user?.colors?.primary || '#3b82f6' }} 
                  />
                  <span className="truncate">{item.value}</span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Social Column */}
        <div className="col-span-1 bg-white p-4 rounded-lg border border-gray-200">
          <h3 
            className="text-sm font-semibold mb-3"
            style={{ color: user?.colors?.text || '#1f2937' }}
          >
            Social
          </h3>
          <div className="space-y-3">
            {(() => {
              const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
              const list = socialLinks
                .filter((s) => allowedSocials.has(normalize(s.platform)))
                .map((s) => ({ ...s, platform: alias(s.platform, s.url) }));
              list.sort((a, b) => PRIORITY.indexOf(alias(String(a.platform))) - PRIORITY.indexOf(alias(String(b.platform))));
              return list.slice(0, 3).map((social, index) => {
                const _fallback = getSocialFallback(social.platform);
                return (
                  <a
                    key={index}
                    href={social.url}
                    onClick={() => onAnalyticsUpdate?.('socialClicks')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-8 h-8 transition-colors"
                  >
                    <UnifiedBrandIcon 
                      platform={social.platform}
                      kind="social"
                      size={20}
                      className=""
                      title={social.platform}
                      color={user?.colors?.accent || '#f59e0b'}
                    />
                  </a>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;